import { Navigate } from 'react-router-dom';
import { useSession } from '../lib/session/sessionContext';
import { Button, EmptyState } from '../ui';
import { navConfig } from './navConfig';
import { resolveLanding } from './resolveLanding';

/**
 * The index (`/`) landing element (F5-b spec FR-12), replacing the interim index → `DashboardRoute`
 * (Dashboard is F17, Phase 2). It redirects to the first `navConfig` destination whose gating key
 * the session holds — the same "first accessible" rule the sidebar hides entries by, so the user
 * lands on a section they can actually see.
 *
 * App chrome, not a lazy feature chunk, so `router.tsx` mounts it eagerly like `AppLayout`.
 */
export function LandingRoute() {
    const session = useSession();

    // This element is only mounted inside `AuthenticatedGate`'s authenticated `<Outlet/>`, so the
    // session is authenticated here. A non-authenticated status means the gate is already
    // redirecting to /login — render nothing rather than flash a landing or an empty state.
    if (session.status !== 'authenticated') {
        return null;
    }

    const destination = resolveLanding(navConfig, (key) => session.hasPermission(key));
    if (destination !== null) {
        // `replace`, so `/` does not sit in history behind the resolved section — Back should not
        // return to a redirect that immediately forwards again.
        return <Navigate to={destination} replace />;
    }

    // Fail-closed (F5-b NFR-4): a session holding no nav key lands nowhere real. Rather than a blank
    // canvas or a redirect loop, name why and offer a re-check — an administrator granting a role
    // is what resolves it, and `refresh()` re-runs the /me bootstrap to pick that grant up. Sign
    // out stays the sidebar current-user menu's single control (F5-b AC-6), so it is not duplicated here.
    return (
        <EmptyState
            message="No sections are available to you yet"
            action={
                <Button
                    variant="secondary"
                    data-testid="app-landing-refresh"
                    onClick={() => {
                        void session.refresh();
                    }}
                >
                    Check again
                </Button>
            }
            data-testid="app-landing-empty"
        />
    );
}
